import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import useActions from '../hooks/useActions';
import './Actions.css';

function formatDate(epoch) {
    if (!epoch) return '';
    const d = new Date(epoch * 1000);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function timeAgo(epoch) {
    if (!epoch) return '';
    const diff = Math.floor(Date.now() / 1000) - epoch;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return formatDate(epoch);
}

function formatMinutes(mins) {
    if (!mins) return null;
    if (mins < 60) return `~${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (m === 0) return `~${h}h`;
    return `~${h}h ${m}m`;
}

export default function ActionsPage() {
    const auth = useAuth();
    const navigate = useNavigate();
    const { actions, loading, fetchActions, completeAction, deleteAction } = useActions();
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        fetchActions();
    }, [fetchActions]);

    const pending = actions.filter(a => a.status === 'pending');
    const completed = actions.filter(a => a.status === 'completed');

    const toggleExpand = (id) => {
        setExpandedId(prev => prev === id ? null : id);
    };

    return (
        <div className="actions-app">
            <header className="actions-topbar">
                <div className="actions-topbar-left">
                    <button className="actions-back" onClick={() => navigate('/')}>
                        <svg viewBox="0 0 24 24"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                    </button>
                    <div className="actions-topbar-brand">Actions</div>
                </div>
                <button className="actions-topbar-signout" onClick={auth.signOut}>
                    Sign out
                </button>
            </header>

            <div className="actions-content">
                {loading && actions.length === 0 ? (
                    <div className="actions-empty">Loading...</div>
                ) : actions.length === 0 ? (
                    <div className="actions-empty">
                        <h2>No actions yet</h2>
                        <p>Your coach will suggest actions during your conversations. Head back to chat to get started.</p>
                        <button className="actions-cta" onClick={() => navigate('/')}>Back to chat</button>
                    </div>
                ) : (
                    <>
                        {pending.length > 0 && (
                            <section className="actions-section">
                                <h3 className="actions-section-label">To do</h3>
                                {pending.map(a => (
                                    <ActionItem
                                        key={a.action_id}
                                        action={a}
                                        expanded={expandedId === a.action_id}
                                        onToggle={() => toggleExpand(a.action_id)}
                                        onComplete={() => completeAction(a.action_id)}
                                        onDelete={() => deleteAction(a.action_id)}
                                    />
                                ))}
                            </section>
                        )}
                        {completed.length > 0 && (
                            <section className="actions-section">
                                <h3 className="actions-section-label">Done</h3>
                                {completed.map(a => (
                                    <ActionItem
                                        key={a.action_id}
                                        action={a}
                                        expanded={expandedId === a.action_id}
                                        onToggle={() => toggleExpand(a.action_id)}
                                        onDelete={() => deleteAction(a.action_id)}
                                    />
                                ))}
                            </section>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

function ActionItem({ action, expanded, onToggle, onComplete, onDelete }) {
    const isPending = action.status === 'pending';

    return (
        <div className={`action-item ${action.status}`}>
            <div className="action-row">
                {isPending ? (
                    <div className="action-check" onClick={onComplete}>
                        <div className="action-checkbox" />
                    </div>
                ) : (
                    <div className="action-check done">
                        <svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg>
                    </div>
                )}
                <div className="action-text">{action.text}</div>
                <button className="action-expand" onClick={onToggle}>
                    <svg viewBox="0 0 24 24" className={expanded ? 'rotated' : ''}>
                        <path d="M9 18l6-6-6-6" />
                    </svg>
                </button>
            </div>
            {expanded && (
                <div className="action-detail">
                    {formatMinutes(action.estimated_minutes) && (
                        <div className="action-detail-row">
                            <span className="action-detail-label">Estimated time</span>
                            <span className="action-detail-value">{formatMinutes(action.estimated_minutes)}</span>
                        </div>
                    )}
                    <div className="action-detail-row">
                        <span className="action-detail-label">Created</span>
                        <span className="action-detail-value">{formatDate(action.created_at)} ({timeAgo(action.created_at)})</span>
                    </div>
                    {action.status === 'completed' && action.completed_at > 0 && (
                        <div className="action-detail-row">
                            <span className="action-detail-label">Completed</span>
                            <span className="action-detail-value">{formatDate(action.completed_at)} ({timeAgo(action.completed_at)})</span>
                        </div>
                    )}
                    <div className="action-detail-row">
                        <span className="action-detail-label">Status</span>
                        <span className={`action-status-badge ${action.status}`}>
                            {isPending ? 'Pending' : 'Completed'}
                        </span>
                    </div>
                    <button className="action-delete" onClick={onDelete}>
                        Delete action
                    </button>
                </div>
            )}
        </div>
    );
}
